/**
 * Prevê, ANTES de salvar, o que a edição do valor de um atendimento vai fazer com a cobrança.
 *
 * Espelha `BillingService.adjustAppointmentTotal` (backend). O operador precisa saber, no
 * momento em que mexe no valor, que aquilo não vai simplesmente "corrigir o preço": com a
 * cobrança já paga, subir o valor cria uma cobrança complementar e baixá-lo abre uma
 * solicitação de estorno — decisões que ele não pode descobrir só depois de salvar.
 *
 * ⚠️ Se a regra do backend mudar, esta função tem que mudar junto, senão o aviso mente.
 */

import { isClosedBillingStatus } from './appointment-billing-state'

export type AppointmentAdjustmentAction =
    | 'NONE'          // nada muda
    | 'UPDATED'       // valor apenas corrigido na cobrança (não há dinheiro pago envolvido)
    | 'EXTRA_CHARGE'  // diferença vira cobrança complementar
    | 'REFUND'        // diferença volta ao cliente (fila de estorno, com aprovação)

export interface AdjustmentPreviewBilling {
    /**
     * A cobrança do atendimento em edição. Não entra na previsão de valor — está aqui
     * porque a mesma cobrança é o alvo da projeção de repasse (a troca de executante
     * move a conta a pagar que nasceu dela).
     */
    id?: number
    status: string
    amount: number
    /**
     * Pagador. Em `HEALTH_PLAN` o `amount` vale ZERO de propósito e o valor do
     * atendimento está em `invoicedAmount` — comparar contra o `amount` faria toda
     * edição parecer um aumento do valor cheio.
     */
    payerType?: string | null
    invoicedAmount?: number | null
    /** Taxa embutida no `amount` (política de taxa por empresa). Fica FORA da comparação. */
    feeAmount?: number | null
    adjustments?: Array<{
        status: string
        amount: number
        feeAmount?: number | null
    }> | null
    /**
     * Fatias, quando a cobrança cobre mais de um atendimento (o paciente que marca em
     * três lugares e paga uma vez). Com elas, o `amount` da cobrança é a SOMA de todos
     * e não serve de base para comparar o valor de um. Ver docs/billing-payout-share.md.
     */
    payoutShares?: Array<{
        sourceType: string
        sourceId: number
        amount: number
    }> | null
}

/** Identifica, numa cobrança compartilhada, qual atendimento está sendo editado. */
export interface AdjustmentPreviewSource {
    sourceType: string
    sourceId: number
}

export interface AppointmentAdjustmentPreview {
    action: AppointmentAdjustmentAction
    /** Diferença em centavos (sempre positiva) que será cobrada a mais ou devolvida. */
    amount: number
    /**
     * Quanto ESTE atendimento vale hoje. Numa cobrança compartilhada é a fatia dele —
     * não o total da cobrança, que pertence também aos outros.
     */
    currentTotal: number
    /** A cobrança cobre mais de um atendimento. */
    shared: boolean
    /** Total da cobrança inteira hoje. Só quando `shared`. */
    groupCurrentTotal?: number
    /** Total da cobrança inteira depois de salvar. Só quando `shared`. */
    groupNewTotal?: number
    /** Quantos atendimentos a cobrança cobre. Só quando `shared`. */
    groupCount?: number
}

const DEAD = ['CANCELLED', 'WRITTEN_OFF']
const UNPAID = ['PENDING', 'OVERDUE']
const UNTOUCHED = ['PENDING', 'OVERDUE', 'IN_DISPUTE']

export function previewAppointmentAdjustment(
    billing: AdjustmentPreviewBilling | null | undefined,
    newTotal: number,
    source?: AdjustmentPreviewSource | null,
): AppointmentAdjustmentPreview | null {
    if (!billing) return null

    /**
     * COBRANÇA ENCERRADA (cancelada, baixada como perda, estornada): nada a anunciar.
     *
     * O status da cobrança PRINCIPAL não era olhado em lugar nenhum — só o dos
     * complementos —, então uma cobrança CANCELLED não passava pelo teste `UNTOUCHED`
     * e caía no caminho da cobrança paga: o aviso anunciava "nova cobrança de R$ 35,00
     * — o atendimento já tem cobrança paga de R$ 665,00", sendo que aquela cobrança
     * estava cancelada e ninguém a pagou. O backend fazia o mesmo e criava mesmo o
     * complemento; ambos foram corrigidos (`BillingService.adjustAppointmentTotal`).
     */
    if (isClosedBillingStatus(billing.status)) return null

    /**
     * COBERTO POR CONVÊNIO: só corrige o valor faturado — nunca complementa nem estorna.
     *
     * Espelha o desvio de `BillingService.adjustAppointmentTotal`. A cobrança consta
     * `PAID` com `amount` zero (nasce quitada para liberar o repasse), então o cálculo
     * normal comparava o novo valor contra ZERO e anunciava "Nova cobrança de R$ 38,31"
     * — o valor inteiro do atendimento, como se fosse um acréscimo. E não há mesmo o que
     * complementar: ninguém pagou nada ainda, o convênio paga a fatura do período.
     *
     * Quando a fatura já tiver sido fechada e enviada (Fase 2), aí sim mudar o valor
     * exigirá lançamento novo — o que já foi apresentado ao convênio não se altera.
     */
    if (billing.payerType === 'HEALTH_PLAN') {
        const invoiced = billing.invoicedAmount ?? 0
        const diff = newTotal - invoiced
        return {
            action: diff === 0 ? 'NONE' : 'UPDATED',
            amount: Math.abs(diff),
            currentTotal: invoiced,
            shared: false,
        }
    }

    const live = (billing.adjustments || []).filter(a => !DEAD.includes(a.status))

    // A taxa sai da conta: `amount` do backend é "valor do atendimento + taxa", e o valor
    // que a tela calcula é o do atendimento. Comparar um contra o outro anunciaria um
    // estorno do tamanho da taxa mesmo sem ninguém ter mexido no preço.
    const net = (b: { amount: number; feeAmount?: number | null }): number =>
        b.amount - (b.feeAmount ?? 0)

    const billingTotal = net(billing) + live.reduce((sum, a) => sum + net(a), 0)

    /**
     * COBRANÇA COMPARTILHADA (cobre vários atendimentos).
     *
     * O `amount` é a soma de TODOS, então não serve de base: editar um exame de
     * R$ 155,55 numa cobrança de R$ 191,55 anunciava "a cobrança será atualizada de
     * R$ 191,55 para R$ 155,55" — como se o valor tivesse caído R$ 36,00, que é o
     * que o OUTRO atendimento vale. O que muda é só a fatia deste.
     *
     * Espelha `BillingService.adjustAppointmentTotal`, que já fazia certo no salvar:
     * o aviso é que mentia. Sem `source` o backend recusa o ajuste (não dá para saber
     * de quem é o valor), então aqui também não se anuncia nada.
     */
    const shares = billing.payoutShares || []
    const shared = shares.length > 1
    if (shared && !source) return null

    const share = shared
        ? shares.find(
              s =>
                  s.sourceType === source!.sourceType &&
                  s.sourceId === source!.sourceId,
          )
        : undefined

    // Fatia não encontrada numa cobrança que tem várias: é inconsistência de dados, e
    // assumir zero anunciaria "de R$ 0,00 para R$ X". Melhor não afirmar nada.
    if (shared && !share) return null

    const currentTotal = shared ? share!.amount : billingTotal
    const delta = newTotal - currentTotal

    const withGroup = (
        preview: AppointmentAdjustmentPreview,
    ): AppointmentAdjustmentPreview =>
        shared
            ? {
                  ...preview,
                  shared: true,
                  groupCurrentTotal: billingTotal,
                  groupNewTotal: billingTotal + delta,
                  groupCount: shares.length,
              }
            : preview

    // Cobrança sem nenhum pagamento: o valor é só corrigido — não há o que complementar
    // nem devolver. É o caminho da imensa maioria das edições, e não merece aviso.
    if (UNTOUCHED.includes(billing.status) && live.length === 0) {
        return withGroup({
            action: delta === 0 ? 'NONE' : 'UPDATED',
            amount: Math.abs(delta),
            currentTotal,
            shared,
        })
    }

    if (delta === 0)
        return withGroup({ action: 'NONE', amount: 0, currentTotal, shared })

    if (delta > 0) {
        return withGroup({
            action: 'EXTRA_CHARGE',
            amount: delta,
            currentTotal,
            shared,
        })
    }

    // Valor caiu: abate primeiro do que ainda NÃO foi pago (cancelar/reduzir um complemento
    // pendente é melhor do que cobrá-lo e devolver depois). Só o que sobrar é estorno.
    let remaining = -delta
    for (const adj of live) {
        if (remaining <= 0) break
        if (!UNPAID.includes(adj.status)) continue
        remaining -= Math.min(net(adj), remaining)
    }

    if (remaining <= 0)
        return withGroup({
            action: 'UPDATED',
            amount: -delta,
            currentTotal,
            shared,
        })

    return withGroup({
        action: 'REFUND',
        amount: remaining,
        currentTotal,
        shared,
    })
}
